import { CSSProperties, ReactElement } from "react";
import styled from "styled-components";
import media from "styled-media-query";
import { Auth, UserScheme } from "client-v2";
import { useSelector } from "react-redux";

import { Color, Menu, Button, ColorV2 } from "@adoptaunabuelo/react-components";
import Logo from "../../assets/logo/AUA";
import { Power, ArrowLeft, X, UserCircle } from "lucide-react";
import { State } from "../../redux";
import { getAuth } from "firebase/auth";

const screenSize1: any = "1350px";
const screenSize2: any = "1024px";

const Container = styled.div<{
	color?: string;
	$active?: boolean;
	$transparent?: boolean;
}>`
	display: flex;
	align-items: center;
	width: -webkit-fill-available;
	width: -moz-available;
	z-index: 990;
	transition: background-color 0.3s ease;
	background-color: ${(props) =>
		props.color ? props.color : props.$active ? "white" : "transparent"};
	border-bottom: ${(props) =>
		props.$active
			? "1px solid " + Color.line.soft
			: "0px solid " + Color.line.soft};
	height: 56px;
	padding: 4px 150px 4px;
	${media.lessThan(screenSize1)`
        padding: 4px 65px 4px;
    `}
	${media.lessThan(screenSize2)`
        padding: 4px 48px 4px;
    `}
	${(props) => media.lessThan("medium")`
        position: ${props.$transparent ? "absolute" : "relative"};
        background-color: transparent;
		padding: 4px 32px 4px;
    `}
	${media.lessThan("small")`
        padding: 4px 24px 4px;
    `}
`;
const Center = styled.div`
	display: flex;
	flex: 1;
	align-items: center;
	justify-content: center;
`;
const Left = styled.div`
	display: flex;
	align-items: center;
	${(props) => media.lessThan("small")`
        margin-left: 0px;
    `}
`;
const Right = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	${media.lessThan("medium")`
        flex: 1;
    `}
`;
const LogoView = styled.div<{ $backgroundColor?: string }>`
	height: 52px;
	background-color: ${(props) =>
		props.$backgroundColor ? props.$backgroundColor : "white"};
	cursor: pointer;
	padding: 1px;
`;
const MenuView = styled.div`
	margin-right: -12px;
`;
const BackView = styled.div`
	display: none;
	${media.lessThan("small")`
        display: flex;
    `}
`;

const Header = (props: HeaderProps) => {
	const isMobile = useSelector((state: State) => state.screen.isMobile);

	const goHome = () => {
		window.location.href = "https://adoptaunabuelo.org";
	};

	const onOptionClick = async (option: any) => {
		if (option.id === "logout") {
			const auth = getAuth();
			await Auth.logout({ auth: auth });
			window.location.reload();
		}
	};

	return (
		<Container
			style={props.style}
			color={props.backgroundColor}
			$active={props.active}
			$transparent={props.transparent}
		>
			{props.onBackClick && !props.showClose && (
				<BackView>
					<Button
						design="image"
						style={{ height: 44, width: 44, marginLeft: -10 }}
						icon={
							<ArrowLeft
								color={
									props.style?.color
										? props.style.color
										: Color.text.full
								}
							/>
						}
						disabled={props.disableBack}
						onClick={props.onBackClick}
					/>
				</BackView>
			)}
			{props.showClose && (
				<BackView>
					<Button
						design="image"
						style={{ height: 44, width: 44, marginLeft: -10 }}
						icon={
							<X
								color={
									props.style?.color
										? props.style.color
										: Color.text.full
								}
							/>
						}
						onClick={() =>
							window.open("https://adoptaunabuelo.org")
						}
					/>
				</BackView>
			)}
			{props.childrenPosition === "center" ? (
				<Center>
					{!props.hideLogo && !props.onBackClick && (
						<LogoView
							$backgroundColor={props.logoBackgroundColor}
							onClick={goHome}
						>
							<Logo
								size={
									isMobile
										? { width: 119, height: 44 }
										: { width: 160, height: 52 }
								}
								color={props.logoColor}
							/>
						</LogoView>
					)}
					{props.children}
				</Center>
			) : (
				<>
					<Left
						style={
							props.childrenPosition === "left"
								? props.contentStyle
								: {}
						}
					>
						{!props.hideLogo && !props.onBackClick && (
							<LogoView
								$backgroundColor={props.logoBackgroundColor}
								onClick={goHome}
							>
								<Logo
									size={
										isMobile
											? { width: 119, height: 44 }
											: { width: 160, height: 52 }
									}
									color={props.logoColor}
								/>
							</LogoView>
						)}
						{props.childrenPosition === "left" && props.children}
					</Left>
					<Right
						style={
							props.childrenPosition === "right"
								? props.contentStyle
								: {}
						}
					>
						{props.childrenPosition === "right" && props.children}
					</Right>
				</>
			)}
			{props.user && !props.hideUser && (
				<MenuView>
					<Menu
						id={"header-menu-list"}
						icon={
							<UserCircle
								color={
									props.color
										? props.color
										: ColorV2.text.neutralHard
								}
							/>
						}
						menuStyle={{ width: "max-content" }}
						options={[
							{
								id: "logout",
								label: "Cerrar sesión",
								icon: <Power />,
							},
						]}
						position={"bottom-left"}
						onClick={onOptionClick}
					/>
				</MenuView>
			)}
		</Container>
	);
};
export default Header;
export interface HeaderProps {
	style?: CSSProperties;
	contentStyle?: CSSProperties;
	user?: UserScheme;
	active?: boolean;
	children?: ReactElement;
	backgroundColor?: string;
	color?: string;
	childrenPosition?: "left" | "right" | "center";
	hideLogo?: boolean;
	logoBackgroundColor?: string;
	logoColor?: "white" | "black";
	hideUser?: boolean;
	transparent?: boolean;
	disableBack?: boolean;
	showClose?: boolean;
	onBackClick?: () => void;
}
